import React, { useMemo } from "react";
import { store } from "../../store";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatisticCard } from "@/components/ui/statistic-card";
import { ChartPieIcon, SmileIcon, TriangleAlert } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { StandardPieChart } from "@/components/ui/standard-pie-chart";
import { StandardTimelineChart } from "@/components/ui/standard-timeline-chart";
import {
    List,
    ListContent,
    ListDescription,
    ListDrilldown,
    ListHeader,
    ListTabs,
    ListTabsContent,
    ListTitle,
} from "@/components/ui/list";
import { routeIncidents } from "../../routes";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ListEvents } from "@/parts/list-events";
import type { IncidentsPerUser, IncidentsPerVehicle } from "../../api";
import {
    useFetchDashboardSafety,
    useFetchDashboardSafetyIncidentCounts,
} from "@/lib/hooks/dashboard";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router";
import { useFetchEvents } from "@/lib/hooks/event";
import { getPastRangeStart, type PastRange, useTimelineFormatting } from "@/lib/time";
import { useTranslation } from "react-i18next";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from "@/components/ui/empty";

const ListOperatorIncidents = ({ data }: { data: IncidentsPerUser[] }): JSX.Element => {
    const { t } = useTranslation();

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.operator")}
                    </TableHead>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.shift")}
                    </TableHead>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.incidentsCaused")}
                    </TableHead>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.totalDriveTime")}
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {data.map((row) => (
                    <TableRow key={row.userId}>
                        <TableCell padded>
                            {row.userFirstName} {row.userLastName}
                        </TableCell>
                        <TableCell>{row.shift}</TableCell>
                        <TableCell>{row.incidents}</TableCell>
                        <TableCell>{store.i18n.formatDuration(row.totalDriveSeconds)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const ListTruckIncidents = ({ data }: { data: IncidentsPerVehicle[] }): JSX.Element => {
    const { t } = useTranslation();

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.truck")}
                    </TableHead>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.incidentsCaused")}
                    </TableHead>
                    <TableHead>
                        {t("page.dashboard.safetyTab.mostIncidentsCaused.header.totalDriveTime")}
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {data.map((row) => (
                    <TableRow key={row.vehicleId}>
                        <TableCell padded>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        {row.vehicleManufacturer} {row.vehicleModel}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>{row.vehicleSerialNumber}</TooltipContent>
                            </Tooltip>
                        </TableCell>
                        <TableCell>{row.incidents}</TableCell>
                        <TableCell>{store.i18n.formatDuration(row.totalDriveSeconds)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export type DashboardSafetyTabProps = {
    range: DateRange;
};

export const DashboardSafetyTab = ({ range }: DashboardSafetyTabProps): JSX.Element => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const pastRange = useMemo(() => getPastRangeStart(range), [range]);

    const [data] = useFetchDashboardSafety({
        dateStart: range.from!,
        dateEnd: range.to!,
    });
    const [incidentCounts] = useFetchDashboardSafetyIncidentCounts({
        dateStart: range.from!,
        dateEnd: range.to!,
    });
    const [pastIncidentCounts] = useFetchDashboardSafetyIncidentCounts({
        dateStart: pastRange.start,
        dateEnd: range.from!,
    });

    const [events, _isEventsLoading, _, refreshEvents] = useFetchEvents();

    const formatTimelineDate = useTimelineFormatting(range);

    const totalIncidents = (() => {
        if (incidentCounts) {
            return (
                incidentCounts.badgeZoneViolations +
                incidentCounts.vehicleZoneViolations +
                incidentCounts.impacts +
                incidentCounts.stationaryZoneViolations
            );
        } else {
            return 0;
        }
    })();

    const totalPastIncidents = (() => {
        if (pastIncidentCounts) {
            return (
                pastIncidentCounts.badgeZoneViolations +
                pastIncidentCounts.vehicleZoneViolations +
                pastIncidentCounts.impacts +
                pastIncidentCounts.stationaryZoneViolations
            );
        } else {
            return 0;
        }
    })();

    const calculateTrend = (current?: number, previous?: number): number | undefined => {
        if (current == null || previous == null || previous === 0) {
            return undefined;
        }
        return (current - previous) / previous;
    };

    const formatTrendDescription = (
        trend: number | undefined | null,
        pastRange: PastRange,
    ): string => {
        if (trend == null || trend === 0.0) {
            return "";
        }

        const percentage = Math.round(trend * 100);
        const sign = trend > 0 ? "+" : "";
        return `${sign}${percentage}% ${store.i18n.formatPastRange(pastRange)}`;
    };

    return (
        <div>
            <div className="grid w-full grid-cols-12 gap-4">
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.safetyTab.stat.incidents")}
                    description={formatTrendDescription(
                        calculateTrend(totalIncidents, totalPastIncidents),
                        pastRange,
                    )}
                    headerIcon={TriangleAlert}
                    trend={calculateTrend(totalIncidents, totalPastIncidents)}
                    lessIsBetter
                >
                    {totalIncidents}
                </StatisticCard>
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.safetyTab.stat.severeIncidents")}
                    description={formatTrendDescription(
                        calculateTrend(
                            incidentCounts?.severeImpacts,
                            pastIncidentCounts?.severeImpacts,
                        ),
                        pastRange,
                    )}
                    headerIcon={TriangleAlert}
                    trend={calculateTrend(
                        incidentCounts?.severeImpacts,
                        pastIncidentCounts?.severeImpacts,
                    )}
                    lessIsBetter
                >
                    {incidentCounts?.severeImpacts}
                </StatisticCard>
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.safetyTab.stat.zoneViolations")}
                    description={formatTrendDescription(
                        calculateTrend(
                            incidentCounts?.vehicleZoneViolations,
                            pastIncidentCounts?.vehicleZoneViolations,
                        ),
                        pastRange,
                    )}
                    headerIcon={TriangleAlert}
                    trend={calculateTrend(
                        incidentCounts?.vehicleZoneViolations,
                        pastIncidentCounts?.vehicleZoneViolations,
                    )}
                    lessIsBetter
                >
                    {incidentCounts?.vehicleZoneViolations}
                </StatisticCard>
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.safetyTab.stat.vehicleImpacts")}
                    description={formatTrendDescription(
                        calculateTrend(incidentCounts?.impacts, pastIncidentCounts?.impacts),
                        pastRange,
                    )}
                    headerIcon={TriangleAlert}
                    trend={calculateTrend(incidentCounts?.impacts, pastIncidentCounts?.impacts)}
                    lessIsBetter
                >
                    {incidentCounts?.impacts}
                </StatisticCard>
                <Card className="col-span-12 flex max-h-80 flex-col p-4 xl:col-span-4">
                    <CardTitle className="text-base">
                        {t("page.dashboard.safetyTab.incidentsBreakdown.title")}
                    </CardTitle>
                    <CardDescription>
                        {t("page.dashboard.safetyTab.incidentsBreakdown.description")}
                    </CardDescription>
                    <div className="flex h-full min-h-48 items-center justify-center">
                        {totalIncidents > 0 && (
                            <StandardPieChart
                                center={totalIncidents.toString()}
                                centerLabel={t(
                                    "page.dashboard.safetyTab.incidentsBreakdown.chartLine",
                                )}
                                config={{
                                    pedestrianBreach: {
                                        label: t(
                                            "page.dashboard.safetyTab.incidentsBreakdown.pedestrianBreach",
                                        ),
                                        color: "var(--color-red-700)",
                                    },
                                    vehicleBreach: {
                                        label: t(
                                            "page.dashboard.safetyTab.incidentsBreakdown.vehicleBreach",
                                        ),
                                        color: "var(--color-red-500)",
                                    },
                                    impact: {
                                        label: t(
                                            "page.dashboard.safetyTab.incidentsBreakdown.impact",
                                        ),
                                        color: "var(--color-red-300)",
                                    },
                                }}
                                data={[
                                    {
                                        key: "pedestrianBreach",
                                        data: incidentCounts?.badgeZoneViolations ?? 0,
                                    },
                                    {
                                        key: "vehicleBreach",
                                        data: incidentCounts?.vehicleZoneViolations ?? 0,
                                    },
                                    {
                                        key: "impact",
                                        data: incidentCounts?.impacts ?? 0,
                                    },
                                ]}
                            />
                        )}
                        {totalIncidents === 0 && (
                            <div className="h-full w-full pt-4">
                                <Empty>
                                    <EmptyHeader>
                                        <EmptyMedia variant="icon">
                                            <ChartPieIcon />
                                        </EmptyMedia>
                                        <EmptyDescription>
                                            {t("page.dashboard.safetyTab.noIncidents")}
                                        </EmptyDescription>
                                    </EmptyHeader>
                                </Empty>
                            </div>
                        )}
                    </div>
                </Card>
                <List className="col-span-12 max-h-80 xl:col-span-8">
                    <ListHeader>
                        <div>
                            <ListTitle className="text-base">
                                {t("page.dashboard.safetyTab.recentIncidents.title")}
                            </ListTitle>
                            <ListDescription>
                                {t("page.dashboard.safetyTab.recentIncidents.description")}
                            </ListDescription>
                        </div>
                        <ListDrilldown to={routeIncidents} />
                    </ListHeader>
                    <ListContent>
                        <div className="relative flex flex-col overflow-hidden">
                            <ListEvents
                                events={events ?? []}
                                onSelectEvent={(event) => {
                                    if (event) {
                                        navigate(routeIncidents.path(event.id));
                                    }
                                }}
                                refreshEvents={refreshEvents}
                                page={0}
                                setPage={() => {}}
                            />
                        </div>
                    </ListContent>
                </List>
                <List className="col-span-12 h-80 xl:col-span-8">
                    <ListHeader>
                        <div>
                            <ListTitle className="text-base">
                                {t("page.dashboard.safetyTab.mostIncidentsCaused.title")}
                            </ListTitle>
                            <ListDescription>
                                {t("page.dashboard.safetyTab.mostIncidentsCaused.description")}
                            </ListDescription>
                        </div>
                        <ListDrilldown to={routeIncidents} />
                    </ListHeader>
                    <ListTabs className="h-full px-4" defaultValue="operators">
                        <TabsList>
                            <TabsTrigger className="w-full" value="operators">
                                {t("page.dashboard.safetyTab.mostIncidentsCaused.operators")}
                            </TabsTrigger>
                            <TabsTrigger className="w-full" value="trucks">
                                {t("page.dashboard.safetyTab.mostIncidentsCaused.trucks")}
                            </TabsTrigger>
                        </TabsList>
                        <ListTabsContent value="trucks" className="h-full">
                            <ScrollArea className="flex flex-col">
                                {data && data.incidentsPerVehicle.length > 0 && (
                                    <ListTruckIncidents data={data.incidentsPerVehicle} />
                                )}
                            </ScrollArea>
                            {data && data.incidentsPerVehicle.length === 0 && (
                                <Empty>
                                    <EmptyHeader>
                                        <EmptyMedia variant="icon">
                                            <SmileIcon />
                                        </EmptyMedia>
                                        <EmptyDescription>
                                            {t("page.dashboard.safetyTab.noIncidents")}
                                        </EmptyDescription>
                                    </EmptyHeader>
                                </Empty>
                            )}
                        </ListTabsContent>
                        <ListTabsContent value="operators" className="h-full">
                            <ScrollArea className="flex flex-col">
                                {data && data.incidentsPerVehicle.length > 0 && (
                                    <ListOperatorIncidents data={data.incidentsPerUser} />
                                )}
                            </ScrollArea>
                            {data && data.incidentsPerVehicle.length === 0 && (
                                <Empty>
                                    <EmptyHeader>
                                        <EmptyMedia variant="icon">
                                            <SmileIcon />
                                        </EmptyMedia>
                                        <EmptyDescription>
                                            {t("page.dashboard.safetyTab.noIncidents")}
                                        </EmptyDescription>
                                    </EmptyHeader>
                                </Empty>
                            )}
                        </ListTabsContent>
                    </ListTabs>
                    <ListContent></ListContent>
                </List>
                <Card className="col-span-12 flex h-80 flex-col p-4 xl:col-span-4">
                    <CardTitle className="mb-1.5">
                        {t("page.dashboard.safetyTab.incidentsOverTime.title")}
                    </CardTitle>
                    <CardDescription>
                        {t("page.dashboard.safetyTab.incidentsOverTime.description")}
                    </CardDescription>
                    <div className="flex h-full items-center justify-center">
                        {data && (
                            <StandardTimelineChart
                                label={t("page.dashboard.safetyTab.incidentsOverTime.dataPoint")}
                                data={data.incidentsChartData.map((point) => ({
                                    t: formatTimelineDate(point.bucket),
                                    data: point.incidents,
                                }))}
                            />
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};
